import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useReveal from '../hooks/useReveal';
import StatsStrip from '../components/StatsStrip';
import PortfolioGrid from '../components/PortfolioGrid';
import PageMeta from '../components/PageMeta';
import { COMPANY } from '../data/content';

const INSIGHT_SLUGS = ['the-swiss-idea-of-capital', 'the-anatolian-thesis', 'the-quiet-holding-company'];
const INSIGHT_IMAGES = {
  'the-swiss-idea-of-capital': 'https://images.unsplash.com/photo-1546846195-825303d86f02?crop=entropy&cs=srgb&fm=jpg&q=85',
  'the-anatolian-thesis': 'https://images.unsplash.com/photo-1703639948834-342fc34900f8?crop=entropy&cs=srgb&fm=jpg&q=85',
  'the-quiet-holding-company': 'https://images.unsplash.com/photo-1472803828399-39d4ac53c6e5?crop=entropy&cs=srgb&fm=jpg&q=85',
};

export default function Home() {
  const { t } = useTranslation();
  useReveal();

  const principles = t('principles.items', { returnObjects: true }) || [];

  return (
    <div data-testid="home-page">
      <PageMeta pageKey="home" />
      <section data-testid="hero" className="relative min-h-[92vh] flex flex-col justify-center px-6 md:px-12 overflow-hidden bg-[#F5EFE1]">
        <img
          src={COMPANY.markWhite}
          alt=""
          aria-hidden="true"
          className="emblem-drift absolute right-[-6vw] top-1/2 -translate-y-1/2 h-[110vh] w-auto opacity-[0.08] pointer-events-none hidden md:block"
        />

        <div className="relative z-10 max-w-6xl">
          <p className="reveal text-[11px] tracking-[0.35em] uppercase text-[#B58F45] mb-8" data-testid="hero-eyebrow">
            {t('hero.eyebrow')}
          </p>
          <h1 className="reveal font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[92px] text-[#0B1F3F] leading-[1.02] tracking-tight max-w-5xl" data-testid="hero-title">
            {t('hero.title_pre')}
            <br />
            <span className="italic text-[#0B1F3F]/90">{t('hero.title_italic')}</span> {t('hero.title_post')}
          </h1>
          <p className="reveal mt-10 text-lg md:text-xl font-sans text-[#6B5B4A] max-w-2xl font-light leading-relaxed" data-testid="hero-subtitle">
            {t('hero.subtitle')}
          </p>
          <div className="reveal mt-14 flex flex-col sm:flex-row gap-4">
            <Link
              to="/portfolio"
              data-testid="hero-cta-portfolio"
              className="inline-flex items-center justify-center px-9 py-4 bg-[#0B1F3F] text-[#F5EFE1] font-sans text-xs tracking-[0.2em] uppercase hover:bg-[#B58F45] hover:text-[#0B1F3F] transition-colors duration-300"
            >
              {t('hero.cta_portfolio')}
              <ArrowRight size={14} className="ml-3" />
            </Link>
            <Link
              to="/about"
              data-testid="hero-cta-about"
              className="inline-flex items-center justify-center px-9 py-4 border border-[#0B1F3F]/30 text-[#0B1F3F] font-sans text-xs tracking-[0.2em] uppercase hover:border-[#0B1F3F]/60 transition-colors duration-300"
            >
              {t('hero.cta_about')}
            </Link>
          </div>
        </div>
        <div className="absolute bottom-8 left-6 md:left-12 right-6 md:right-12 flex justify-between items-end z-10">
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#6B5B4A]">{t('hero.scroll')}</span>
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#6B5B4A] hidden md:block">
            {t('company.headquarters')}
          </span>
        </div>
      </section>

      <StatsStrip />

      <section data-testid="home-philosophy" className="px-6 md:px-12 py-28 md:py-40">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-4">
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#B58F45] mb-6">{t('philosophy.eyebrow')}</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0B1F3F] leading-[1.05] tracking-tight">
              {t('philosophy.title')}
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="font-serif italic text-2xl md:text-3xl text-[#0B1F3F] leading-relaxed mb-10">
              &ldquo;{t('philosophy.quote')}&rdquo;
            </p>
            <p className="text-[#6B5B4A] text-base leading-relaxed max-w-2xl mb-6">
              {t('philosophy.body')}
            </p>
            <Link to="/about" className="inline-flex items-center gap-3 mt-4 text-xs tracking-[0.25em] uppercase text-[#0B1F3F] border-b border-[#0B1F3F]/25 pb-1 hover:text-[#B58F45] hover:border-[#B58F45] transition-colors">
              {t('philosophy.cta')} <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <section data-testid="home-principles" className="bg-[#EBE3D0] border-y border-[#0B1F3F]/10 py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <p className="text-[10px] tracking-[0.35em] uppercase text-[#B58F45] mb-6">{t('principles.eyebrow')}</p>
          <h2 className="font-serif text-4xl md:text-5xl text-[#0B1F3F] leading-tight tracking-tight max-w-3xl mb-16">
            {t('principles.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[1px] bg-[#0B1F3F]/8 border border-[#0B1F3F]/10">
            {principles.map((p) => (
              <div key={p.number} data-testid={`principle-${p.number}`} className="bg-[#F5EFE1] p-10 md:p-14">
                <p className="font-serif text-6xl text-[#B58F45] mb-6 leading-none">{p.number}</p>
                <h3 className="font-serif text-2xl md:text-3xl text-[#0B1F3F] mb-4">{p.title}</h3>
                <p className="text-[#6B5B4A] text-[15px] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="home-portfolio" className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8">
            <div>
              <p className="text-[10px] tracking-[0.35em] uppercase text-[#B58F45] mb-6">{t('portfolio_home.eyebrow')}</p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#0B1F3F] leading-tight tracking-tight max-w-2xl">
                {t('portfolio_home.title')}
              </h2>
            </div>
            <Link to="/portfolio" className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-[#0B1F3F] hover:text-[#B58F45] transition-colors">
              {t('portfolio_home.cta_all')} <ArrowUpRight size={14} />
            </Link>
          </div>
          <PortfolioGrid />
        </div>
      </section>

      <section data-testid="home-insights" className="py-24 md:py-32 px-6 md:px-12 bg-[#EBE3D0] border-y border-[#0B1F3F]/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8">
            <div>
              <p className="text-[10px] tracking-[0.35em] uppercase text-[#B58F45] mb-6">{t('insights_home.eyebrow')}</p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#0B1F3F] leading-tight tracking-tight max-w-2xl">
                {t('insights_home.title')}
              </h2>
            </div>
            <Link to="/insights" className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-[#0B1F3F] hover:text-[#B58F45] transition-colors">
              {t('insights_home.cta_all')} <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {INSIGHT_SLUGS.map((slug) => (
              <article
                key={slug}
                data-testid={`insight-card-${slug}`}
                className="group cursor-pointer border border-[#0B1F3F]/10 bg-[#F5EFE1] hover:border-[#0B1F3F]/35 transition-colors duration-300"
              >
                <div className="aspect-[16/10] overflow-hidden border-b border-[#0B1F3F]/10">
                  <img src={INSIGHT_IMAGES[slug]} alt={t(`insights.${slug}.headline`)} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms]" />
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-4 text-[10px] font-sans text-[#6B5B4A] mb-4 uppercase tracking-[0.2em]">
                    <span className="text-[#B58F45]">{t(`insights.${slug}.category`)}</span>
                    <span>·</span>
                    <span>{t(`insights.${slug}.date`)}</span>
                  </div>
                  <h3 className="font-serif text-xl md:text-[22px] text-[#0B1F3F] leading-snug group-hover:text-[#B58F45] transition-colors duration-300">
                    {t(`insights.${slug}.headline`)}
                  </h3>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="home-cta" className="py-32 px-6 md:px-12 relative overflow-hidden">
        <img src={COMPANY.markWhite} alt="" aria-hidden="true" className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 h-[80vh] w-auto opacity-[0.04] pointer-events-none" />
        <div className="relative max-w-4xl mx-auto text-center">
          <p className="text-[10px] tracking-[0.35em] uppercase text-[#B58F45] mb-8">{t('home_cta.eyebrow')}</p>
          <h2 className="font-serif text-4xl md:text-6xl text-[#0B1F3F] leading-[1.05] tracking-tight mb-10">
            {t('home_cta.title')}
          </h2>
          <Link to="/contact" data-testid="home-final-cta" className="inline-flex items-center justify-center px-10 py-5 bg-[#B58F45] text-[#0B1F3F] font-sans text-xs tracking-[0.25em] uppercase hover:bg-[#7A1E2E] transition-colors duration-300">
            {t('home_cta.cta')} <ArrowRight size={14} className="ml-3" />
          </Link>
        </div>
      </section>
    </div>
  );
}
